import React, { FunctionComponent } from 'react';
import Capitalize from 'capitalize';
import { map, addIndex } from 'ramda';
import { Label, Header, Grid } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { FormattedNumber, InjectIntlConfig } from 'react-intl';
import classnames from 'classnames';

import { ClassProp } from 'helpers/types';
import {
  TopMetros,
  TopMetrosByAll,
  Type as Indicator,
} from 'components/TopMetros/TopMetros.helpers';
import FormattedCurrency from 'scenes/Report/components/FormattedCurrency';
import { useUserMgmt, UserType } from 'components/UserMgmt/UserMgmt.context';

import styles from './TopMetrosList.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  topMetros: TopMetros[];
  indicator: Indicator;
};

const mapIndexed = addIndex<TopMetrosByAll>(map);

const getCountByIndicator = (
  indicator: Indicator,
  { avgAov, numberOfTransactions, totalRevenue }: TopMetrosByAll & any,
) => {
  if (indicator === 'Sales') {
    return totalRevenue;
  }

  if (indicator === 'Transactions') {
    return numberOfTransactions;
  }

  if (indicator === 'AOV') {
    return avgAov;
  }

  return 0;
};

const renderMetro = (
  userType: UserType,
  city: string,
  state: string,
  index: number,
  count: number,
  FormattedComponent: React.ElementType,
  classes: Styles,
  address = '',
  options: InjectIntlConfig = {},
) => {
  const tier3User = userType === 'tier3';

  return (
    <Grid.Column key={index} width={8} className={classes.city}>
      <Grid container>
        <Grid.Row>
          <Grid.Column width={tier3User ? 2 : 4}>
            <Label className={classes.indexLabel} circular key={city}>
              {index + 1}
            </Label>
          </Grid.Column>

          <Grid.Column
            className={classnames({ [classes.fullLabel]: tier3User })}
            width={tier3User ? 14 : 12}
          >
            {address && (
              <Header className={classes.addressLabel} as="h5">
                {Capitalize.words(address)}
              </Header>
            )}

            <span className={classnames(classes.cityLabel, userType)}>
              {Capitalize.words(city)}, {state}
            </span>

            <Header as="h5" className={classes.countLabel}>
              <FormattedComponent value={count} {...options} />
            </Header>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Grid.Column>
  );
};

const cityMapping = (
  classes: Styles,
  userType: UserType,
  indicator: Indicator,
) =>
  mapIndexed(
    ({ address, city, state, ...counts }: TopMetrosByAll, index: number) => {
      const count = getCountByIndicator(indicator, counts);
      const FormattedComponent =
        indicator === 'Transactions' ? FormattedNumber : FormattedCurrency;

      return renderMetro(
        userType,
        city,
        state,
        index,
        count,
        FormattedComponent,
        classes,
        address,
      );
    },
  ) as Function;

const TopMetrosList: FunctionComponent<Props> = ({
  classes,
  topMetros,
  indicator,
}) => {
  const { userType } = useUserMgmt();

  return (
    <Grid container className={classnames(classes.container, userType)}>
      {cityMapping(classes, userType, indicator)(topMetros)}
    </Grid>
  );
};

export default withStyles(styles)(TopMetrosList);
